import { useRef, useState } from 'react';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MAX_IMPORT_BYTES, canCutOver, formatNzDate, parseAmount } from '@promanage/core';
import { Amount, Button, Chip, Field, Input, Panel, PanelHeader, Select } from '@promanage/ui';
import { TopBar } from '../components/TopBar';
import { LoadFailed } from '../components/QueryState';
import { SettingsNav } from '../features/settings/SettingsNav';
import { useSession } from '../features/auth/session';
import { letterheadLogo } from '../features/branding/letterhead';
import { ExportPdfButton } from '../features/reports/ExportPdfButton';
import { trpc, type RouterOutputs } from '../lib/trpc';

export const Route = createFileRoute('/settings/parallel-run')({
  component: ParallelRunPage,
});

type Status = RouterOutputs['parallelRun']['status'];
type Preview = RouterOutputs['parallelRun']['preview'];
type Run = RouterOutputs['parallelRun']['get'];
type RunLine = Run['lines'][number];
type AnyLine = Preview['lines'][number];
type RunTenancyLine = Run['tenancyLines'][number];
type AnyTenancyLine = Preview['tenancyLines'][number];
type TenancyKind = AnyTenancyLine['kind'];

/** The optional checks, in core's order, with how each names itself when it was not made. */
const TENANCY_CHECKS: readonly { kind: TenancyKind; label: string; unit: string }[] = [
  { kind: 'paid_to', label: 'Paid-to dates', unit: 'tenancy' },
  { kind: 'bond_held', label: 'Bond held in trust', unit: 'bond' },
  { kind: 'bond_lodged', label: 'Bond lodged', unit: 'bond' },
];

/** The reasons, in core's order. Written out so the page does not import the comparison itself. */
const REASONS = [
  ['timing', 'Timing'],
  ['fee', 'Fee difference'],
  ['old_system_error', 'Old system is wrong'],
  ['our_error', 'This system is wrong'],
  ['other', 'Other'],
] as const;
type Reason = (typeof REASONS)[number][0];

const STATE_CHIP = {
  agrees: { tone: 'positive', label: 'Agrees to the cent' },
  explained: { tone: 'neutral', label: 'Differences explained' },
  unexplained: { tone: 'warn', label: 'Differences to explain' },
} as const;

/**
 * The parallel run — this system against the one being left, owner by owner, as at a day.
 *
 * Opening balances proved day zero. This asks the same question after both systems have processed
 * the same month: does each owner still hold the same money in both. See `parallelRun.ts` in core.
 *
 * TWO MORE CHECKS RIDE ON A RUN WHEN THEIR FILES ARE GIVEN — each tenancy's paid-to date, and its
 * bond. Owner balances can all agree while a week's rent sits on the wrong tenant, and no owner
 * line can see a bond. A check that was not made is SAID to be not made, on the preview, the run
 * and the PDF; silence there would read as agreement.
 *
 * NOTHING HERE CHANGES A BALANCE. It reads the ledger and records what it compared. A difference
 * that turns out to be ours is put right by a journal, and shown to be right by running this again.
 *
 * REACHED FROM OPENING BALANCES, not from a tab, for that page's reason: it is the step after it,
 * done a handful of times in an agency's life.
 */
function ParallelRunPage() {
  const session = useSession();

  if (!canCutOver(session?.roles ?? [])) {
    return (
      <Frame>
        <Panel className="p-6">
          <h2 className="text-[1.125rem] font-bold">A parallel run needs a trust administrator</h2>
          <p className="mt-1 max-w-prose text-[0.875rem] text-ink-muted">
            It sets every owner&rsquo;s balance here beside the old system&rsquo;s. Ask a trust
            administrator to run it.
          </p>
        </Panel>
      </Frame>
    );
  }

  return <ParallelRun />;
}

function ParallelRun() {
  const status = useQuery(trpc.parallelRun.status.queryOptions());
  const [openId, setOpenId] = useState<string | null>(null);

  if (status.isPending) {
    return (
      <Frame>
        <p className="text-[0.8125rem] text-ink-muted">Loading…</p>
      </Frame>
    );
  }
  if (status.error) {
    return (
      <Frame>
        <LoadFailed retry={() => void status.refetch()} retrying={status.isFetching} />
      </Frame>
    );
  }

  const data = status.data;

  if (data.cutover === null) {
    return (
      <Frame>
        <Panel className="p-6">
          <h2 className="text-[1.125rem] font-bold">Bring the opening balances across first</h2>
          <p className="mt-1 max-w-prose text-[0.875rem] text-ink-muted">
            A parallel run compares two systems that started from the same balances. Until the money
            has come across there is nothing here to compare.
          </p>
        </Panel>
      </Frame>
    );
  }

  return (
    <Frame>
      <div className="flex flex-col gap-4">
        <NewRun status={data} onRecorded={setOpenId} />
        <Runs runs={data.runs} openId={openId} onOpen={setOpenId} />
        {openId ? <RunDetail id={openId} /> : null}
      </div>
    </Frame>
  );
}

function Frame({ children }: { children: React.ReactNode }) {
  return (
    <div className="mx-auto max-w-[var(--content-max)] pb-4">
      <TopBar
        title="Parallel run"
        subtitle="Check that this system and the one you are leaving hold the same money for each owner, and agree on each tenancy"
        actions={
          <Link to="/settings/opening-balances">
            <Button size="sm" variant="secondary">
              Opening balances
            </Button>
          </Link>
        }
      />
      <SettingsNav />
      {children}
    </div>
  );
}

/** An amount field's cents, or null while it does not parse. Never through a float. */
function cents(text: string): bigint | null {
  if (text.trim() === '') return null;
  try {
    return parseAmount(text);
  } catch {
    return null;
  }
}

// ── A new comparison ─────────────────────────────────────────────────────────

function NewRun({ status, onRecorded }: { status: Status; onRecorded: (id: string) => void }) {
  const queryClient = useQueryClient();

  const [sourceSystem, setSourceSystem] = useState(status.cutover?.sourceSystem ?? '');
  const [asAt, setAsAt] = useState('');
  const [oldTotal, setOldTotal] = useState('');
  const [file, setFile] = useState<PickedFile | null>(null);
  const [paidToFile, setPaidToFile] = useState<PickedFile | null>(null);
  const [bondsFile, setBondsFile] = useState<PickedFile | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  const preview = useMutation(
    trpc.parallelRun.preview.mutationOptions({ meta: { errorTitle: 'Could not compare' } }),
  );
  const commit = useMutation(
    trpc.parallelRun.commit.mutationOptions({
      meta: { errorTitle: 'Nothing was recorded' },
      onSuccess(result) {
        void queryClient.invalidateQueries({ queryKey: trpc.parallelRun.status.queryKey() });
        preview.reset();
        onRecorded(result.id);
      },
    }),
  );

  const read = (chosen: File | undefined, keep: (picked: PickedFile) => void) => {
    setProblem(null);
    if (!chosen) return;
    if (chosen.size > MAX_IMPORT_BYTES) {
      setProblem('That file is larger than an import can take. Split it and try again.');
      return;
    }
    void chosen
      .text()
      .then((csv) => {
        keep({ name: chosen.name, csv });
        // A different file is a different comparison; what was shown no longer describes it.
        preview.reset();
      })
      .catch(() => setProblem('That file could not be read. Save it again as CSV and retry.'));
  };

  const oldTotalCents = cents(oldTotal);
  const request =
    sourceSystem.trim() !== '' && asAt !== '' && oldTotalCents !== null && file
      ? {
          sourceSystem,
          asAt,
          oldSystemTotalCents: oldTotalCents.toString(),
          balancesCsv: file.csv,
          paidToCsv: paidToFile?.csv ?? null,
          bondsCsv: bondsFile?.csv ?? null,
        }
      : null;

  const result = preview.data;

  return (
    <Panel className="p-0">
      <PanelHeader
        title="Compare"
        subtitle={`Owner balances from the old system, against the ledger here as at the same day — and paid-to dates and bonds, if you have them. Opening balances came across on ${formatNzDate(status.cutover?.cutoverOn ?? status.today)}.`}
        className="px-5 pt-5 pb-3"
      />

      <div className="flex flex-col gap-3 px-5 pb-5">
        <div className="flex flex-wrap items-end gap-3">
          <Field label="Old system" required>
            {(props) => (
              <Input
                {...props}
                value={sourceSystem}
                onChange={(e) => setSourceSystem(e.target.value)}
                className="w-48"
              />
            )}
          </Field>
          <Field label="Balances as at" required>
            {(props) => (
              <Input
                {...props}
                type="date"
                max={status.today}
                value={asAt}
                onChange={(e) => {
                  setAsAt(e.target.value);
                  preview.reset();
                }}
                className="w-40"
              />
            )}
          </Field>
          <Field
            label="Old system’s total"
            required
            hint="What its reconciliation report says it owes owners."
          >
            {(props) => (
              <Input
                {...props}
                inputMode="decimal"
                value={oldTotal}
                onChange={(e) => {
                  setOldTotal(e.target.value);
                  preview.reset();
                }}
                placeholder="0.00"
                className="w-40"
              />
            )}
          </Field>
        </div>

        <FilePick
          file={file}
          choose="Choose the owner balances file"
          hint="The same file as at cutover: Owner, Property (optional), Balance."
          onPick={(chosen) => read(chosen, setFile)}
        />
        <FilePick
          file={paidToFile}
          choose="Add paid-to dates"
          hint="Optional. Tenancy, Paid to — as at the same day."
          onPick={(chosen) => read(chosen, setPaidToFile)}
          onClear={() => {
            setPaidToFile(null);
            preview.reset();
          }}
        />
        <FilePick
          file={bondsFile}
          choose="Add bonds"
          hint="Optional. Tenancy, then Bond held (in the trust account), Bond lodged (with Tenancy Services), or both."
          onPick={(chosen) => read(chosen, setBondsFile)}
          onClear={() => {
            setBondsFile(null);
            preview.reset();
          }}
        />
        {problem ? (
          <p role="alert" className="text-[0.75rem] font-medium text-danger">
            {problem}
          </p>
        ) : null}

        <div className="flex flex-wrap items-center gap-2">
          <Button
            size="sm"
            disabled={request === null || preview.isPending}
            onClick={() => request && preview.mutate(request)}
          >
            {preview.isPending ? 'Comparing' : 'Compare'}
          </Button>
          {result?.ready && request ? (
            <Button
              size="sm"
              variant="secondary"
              disabled={commit.isPending}
              onClick={() => commit.mutate(request)}
            >
              {commit.isPending ? 'Recording' : 'Record this run'}
            </Button>
          ) : null}
          <span className="text-[0.75rem] text-ink-muted">
            Comparing changes nothing. Recording keeps both sets of figures as they stand today.
          </span>
        </div>
      </div>

      {result ? <PreviewResult result={result} /> : null}
    </Panel>
  );
}

interface PickedFile {
  name: string;
  csv: string;
}

/** One file of the comparison. Its own hidden input, so three of them cannot share a ref. */
function FilePick({
  file,
  choose,
  hint,
  onPick,
  onClear,
}: {
  file: PickedFile | null;
  choose: string;
  hint: string;
  onPick: (chosen: File | undefined) => void;
  /** Present on the optional files: a check can be taken back out of the comparison. */
  onClear?: () => void;
}) {
  const input = useRef<HTMLInputElement>(null);

  return (
    <div className="flex flex-wrap items-center gap-2">
      <input
        ref={input}
        type="file"
        accept=".csv,text/csv,text/plain"
        className="sr-only"
        onChange={(e) => {
          onPick(e.target.files?.[0]);
          // The same file chosen again after a clear has to fire a change.
          e.target.value = '';
        }}
      />
      {file ? <Chip tone="positive">{file.name}</Chip> : null}
      <Button size="sm" variant="secondary" onClick={() => input.current?.click()}>
        {file ? 'Choose another file' : choose}
      </Button>
      {file && onClear ? (
        <Button size="sm" variant="ghost" onClick={onClear}>
          Remove
        </Button>
      ) : null}
      <span className="text-[0.75rem] text-ink-subtle">{hint}</span>
    </div>
  );
}

function PreviewResult({ result }: { result: Preview }) {
  return (
    <div className="border-t border-rule">
      {result.blockers.length > 0 ? (
        <ul className="flex flex-col gap-1 px-5 py-3 text-[0.8125rem] text-danger" role="alert">
          {result.blockers.map((issue) => (
            <li key={issue.sentence}>{issue.sentence}</li>
          ))}
        </ul>
      ) : null}
      {result.warnings.length > 0 ? (
        <ul className="flex flex-col gap-1 px-5 py-3 text-[0.8125rem] text-warn">
          {result.warnings.map((warning) => (
            <li key={warning}>{warning}</li>
          ))}
        </ul>
      ) : null}
      {result.totals ? (
        <>
          <Totals
            theirs={result.totals.theirTotalCents}
            ours={result.totals.ourTotalCents}
            difference={result.totals.differenceCents}
            differing={result.totals.differingCount}
            lines={result.totals.lineCount}
          />
          <LinesTable lines={result.lines} />
          <TenancyChecks lines={result.tenancyLines} compared={result.compared} />
        </>
      ) : null}
    </div>
  );
}

function Totals({
  theirs,
  ours,
  difference,
  differing,
  lines,
}: {
  theirs: string;
  ours: string;
  difference: string;
  differing: number;
  lines: number;
}) {
  const gap = BigInt(difference);
  return (
    <dl className="grid grid-cols-2 gap-px bg-rule text-[0.8125rem] sm:grid-cols-4">
      <div className="bg-surface px-5 py-3">
        <dt className="text-ink-muted">Old system</dt>
        <dd>
          <Amount value={BigInt(theirs)} className="font-semibold" />
        </dd>
      </div>
      <div className="bg-surface px-5 py-3">
        <dt className="text-ink-muted">Here</dt>
        <dd>
          <Amount value={BigInt(ours)} className="font-semibold" />
        </dd>
      </div>
      <div className="bg-surface px-5 py-3">
        <dt className="text-ink-muted">Here less old system</dt>
        <dd>
          <Amount value={gap} tone={gap === 0n ? 'neutral' : 'warn'} className="font-semibold" />
        </dd>
      </div>
      <div className="bg-surface px-5 py-3">
        <dt className="text-ink-muted">Lines that differ</dt>
        <dd className="font-semibold tabular-nums">
          {differing} of {lines}
        </dd>
      </div>
    </dl>
  );
}

/** Who a line is about — the owner, and the house when the line is one house. */
function LineSubject({ line }: { line: AnyLine }) {
  return (
    <>
      <span className="font-semibold">{line.ownerName}</span>{' '}
      <span className="font-mono text-[0.6875rem] text-ink-subtle">{line.ownerRef}</span>
      <span className="block text-[0.75rem] text-ink-muted">
        {line.scope === 'owner'
          ? 'Everything held for them'
          : (line.property ?? 'Against no particular property')}
      </span>
    </>
  );
}

function LineFigures({ line }: { line: AnyLine }) {
  const difference = BigInt(line.differenceCents);
  return (
    <>
      <td className="px-3 py-2 text-right">
        {/* Absent is not zero: the file had no row for this, and the cell says so. */}
        {line.theirCents === null ? (
          <span className="text-ink-subtle">not in file</span>
        ) : (
          <Amount value={BigInt(line.theirCents)} />
        )}
      </td>
      <td className="px-3 py-2 text-right">
        <Amount value={BigInt(line.ourCents)} />
      </td>
      <td className="px-3 py-2 text-right">
        {difference === 0n ? (
          <span className="text-ink-subtle">—</span>
        ) : (
          <Amount value={difference} tone="warn" />
        )}
      </td>
    </>
  );
}

const TABLE_HEAD = (
  <thead>
    <tr className="text-left text-[0.6875rem] tracking-wide text-ink-muted uppercase">
      <th className="px-5 py-2 font-medium">Owner</th>
      <th className="px-3 py-2 text-right font-medium">Old system</th>
      <th className="px-3 py-2 text-right font-medium">Here</th>
      <th className="px-3 py-2 text-right font-medium">Difference</th>
      <th className="px-5 py-2 font-medium">Status</th>
    </tr>
  </thead>
);

function LinesTable({ lines }: { lines: AnyLine[] }) {
  return (
    <div className="overflow-x-auto border-t border-rule">
      <table className="w-full text-[0.8125rem]">
        {TABLE_HEAD}
        <tbody>
          {lines.map((line) => (
            <tr
              key={`${line.ownerRef}|${line.property ?? line.scope}`}
              className="border-t border-rule"
            >
              <td className="px-5 py-2">
                <LineSubject line={line} />
              </td>
              <LineFigures line={line} />
              <td className="px-5 py-2">
                <Chip tone={line.status === 'agrees' ? 'positive' : 'warn'}>
                  {line.statusLabel}
                </Chip>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Paid-to dates and bonds ──────────────────────────────────────────────────

const days = (n: number): string => `${String(Math.abs(n))} ${Math.abs(n) === 1 ? 'day' : 'days'}`;

/** The two figures of a tenancy line and the gap between them — dates for rent, money for a bond. */
function TenancyFigures({ line }: { line: AnyTenancyLine }) {
  if (line.kind === 'paid_to') {
    const partPaid = line.ourCents === null ? 0n : BigInt(line.ourCents);
    return (
      <>
        <td className="px-3 py-2 text-right tabular-nums">
          {line.theirDate === null ? (
            <span className="text-ink-subtle">not in file</span>
          ) : (
            formatNzDate(line.theirDate)
          )}
        </td>
        <td className="px-3 py-2 text-right tabular-nums">
          {line.ourDate === null ? (
            <span className="text-ink-subtle">no rent here</span>
          ) : (
            formatNzDate(line.ourDate)
          )}
          {/* Not compared — shown because a part payment is the usual reason two dates differ. */}
          {partPaid > 0n ? (
            <span className="block text-[0.75rem] text-ink-muted">
              and <Amount value={partPaid} /> toward the next
            </span>
          ) : null}
        </td>
        <td className="px-3 py-2 text-right">
          {line.differenceDays === null || line.differenceDays === 0 ? (
            <span className="text-ink-subtle">—</span>
          ) : (
            <span className="text-warn">
              {days(line.differenceDays)} {line.differenceDays > 0 ? 'further here' : 'behind here'}
            </span>
          )}
        </td>
      </>
    );
  }

  const difference = BigInt(line.differenceCents ?? '0');
  return (
    <>
      <td className="px-3 py-2 text-right">
        {line.theirCents === null ? (
          <span className="text-ink-subtle">not in file</span>
        ) : (
          <Amount value={BigInt(line.theirCents)} />
        )}
      </td>
      <td className="px-3 py-2 text-right">
        <Amount value={BigInt(line.ourCents ?? '0')} />
      </td>
      <td className="px-3 py-2 text-right">
        {difference === 0n ? (
          <span className="text-ink-subtle">—</span>
        ) : (
          <Amount value={difference} tone="warn" />
        )}
      </td>
    </>
  );
}

function TenancySubject({ line }: { line: AnyTenancyLine }) {
  return (
    <>
      <span className="font-semibold">{line.address}</span>{' '}
      <span className="font-mono text-[0.6875rem] text-ink-subtle">{line.tenancyRef}</span>
    </>
  );
}

/**
 * One section per optional check. A check that was not made still has its section, saying so:
 * leaving it out would let a run with no bond file read as a run whose bonds agreed.
 */
function TenancyChecks<L extends AnyTenancyLine>({
  lines,
  compared,
  row,
}: {
  lines: readonly L[];
  compared: Preview['compared'];
  /** How a line is drawn. Defaults to a plain row; a recorded run supplies one that can be explained. */
  row?: (line: L) => React.ReactNode;
}) {
  return (
    <>
      {TENANCY_CHECKS.map((check) => {
        const own = lines.filter((line) => line.kind === check.kind);
        const differing = own.filter((line) => line.status !== 'agrees').length;

        return (
          <section key={check.kind} className="border-t border-rule">
            <h3 className="flex flex-wrap items-baseline gap-x-2 px-5 pt-3 pb-2 text-[0.8125rem] font-semibold">
              {check.label}
              <span className="font-normal text-ink-muted">
                {compared[check.kind]
                  ? `${String(differing)} of ${String(own.length)} differ`
                  : 'Not compared in this run — no file was given'}
              </span>
            </h3>
            {compared[check.kind] && own.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-[0.8125rem]">
                  <thead>
                    <tr className="text-left text-[0.6875rem] tracking-wide text-ink-muted uppercase">
                      <th className="px-5 py-2 font-medium">Tenancy</th>
                      <th className="px-3 py-2 text-right font-medium">Old system</th>
                      <th className="px-3 py-2 text-right font-medium">Here</th>
                      <th className="px-3 py-2 text-right font-medium">Difference</th>
                      <th className="px-5 py-2 font-medium">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {own.map((line) =>
                      row ? (
                        row(line)
                      ) : (
                        <tr
                          key={`${line.kind}|${line.tenancyRef}`}
                          className="border-t border-rule"
                        >
                          <td className="px-5 py-2">
                            <TenancySubject line={line} />
                          </td>
                          <TenancyFigures line={line} />
                          <td className="px-5 py-2">
                            <Chip tone={line.status === 'agrees' ? 'positive' : 'warn'}>
                              {line.statusLabel}
                            </Chip>
                          </td>
                        </tr>
                      ),
                    )}
                  </tbody>
                </table>
              </div>
            ) : null}
          </section>
        );
      })}
    </>
  );
}

// ── Runs already made ────────────────────────────────────────────────────────

function Runs({
  runs,
  openId,
  onOpen,
}: {
  runs: Status['runs'];
  openId: string | null;
  onOpen: (id: string | null) => void;
}) {
  if (runs.length === 0) return null;

  return (
    <Panel className="p-0">
      <PanelHeader
        title="Runs recorded"
        subtitle="Each one keeps both sets of figures as they stood when it was made."
        className="px-5 pt-5 pb-3"
      />
      <ul>
        {runs.map((run) => {
          const chip = STATE_CHIP[run.state];
          return (
            <li
              key={run.id}
              className={`flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-rule px-5 py-2.5 text-[0.8125rem] ${run.id === openId ? 'bg-accent-soft' : ''}`}
            >
              <Chip tone={chip.tone}>{chip.label}</Chip>
              <span className="font-semibold">As at {formatNzDate(run.asAt)}</span>
              <span className="text-ink-muted">
                {run.sourceSystem} · {run.differingCount + run.tenancyDifferingCount} of{' '}
                {run.lineCount + run.tenancyLineCount} differ
                {run.unexplainedCount > 0 ? ` · ${String(run.unexplainedCount)} to explain` : ''}
                {run.performedByName ? ` · ${run.performedByName}` : ''}
              </span>
              <span className="ml-auto">
                <Button
                  size="sm"
                  variant={run.id === openId ? 'ghost' : 'secondary'}
                  onClick={() => onOpen(run.id === openId ? null : run.id)}
                >
                  {run.id === openId ? 'Close' : 'Open'}
                </Button>
              </span>
            </li>
          );
        })}
      </ul>
    </Panel>
  );
}

function RunDetail({ id }: { id: string }) {
  const run = useQuery(trpc.parallelRun.get.queryOptions({ id }));
  const [activeLine, setActiveLine] = useState<string | null>(null);
  const orgs = useQuery(trpc.auth.organisations.queryOptions(undefined, { staleTime: 60_000 }));
  const orgName = orgs.data?.find((org) => org.isCurrent)?.name ?? null;

  if (run.isPending) return <p className="text-[0.8125rem] text-ink-muted">Loading…</p>;
  if (run.error) {
    return <LoadFailed retry={() => void run.refetch()} retrying={run.isFetching} />;
  }

  const data = run.data;
  const chip = STATE_CHIP[data.state];

  return (
    <Panel className="p-0">
      <PanelHeader
        title={`As at ${formatNzDate(data.asAt)}, against ${data.sourceSystem}`}
        subtitle={
          data.reconciledThrough === null || data.reconciledThrough < data.asAt
            ? 'The trust account was not reconciled here through that day when this run was made.'
            : `The trust account was reconciled here to ${formatNzDate(data.reconciledThrough)} when this run was made.`
        }
        className="px-5 pt-5 pb-3"
        aside={
          <span className="flex flex-wrap items-center gap-2">
            <Chip tone={chip.tone}>{chip.label}</Chip>
            <ExportPdfButton
              label="Download PDF"
              failureTitle="Could not build the report"
              onExport={async () => {
                // Fetched by the CLICK — jspdf must never reach the eager graph (DESIGN_SYSTEM §8).
                const { exportParallelRunPdf } =
                  await import('../features/parallelRun/parallelRunPdf');
                exportParallelRunPdf({
                  // The run as recorded, with whatever has been explained since. Not a fresh
                  // comparison: the page prints the figures this run froze.
                  run: data,
                  orgName,
                  logo: await letterheadLogo(),
                  generatedAt: new Date(),
                });
              }}
            />
          </span>
        }
      />
      <Totals
        theirs={data.theirTotalCents}
        ours={data.ourTotalCents}
        difference={data.differenceCents}
        differing={data.differingCount}
        lines={data.lineCount}
      />
      <div className="overflow-x-auto border-t border-rule">
        <table className="w-full text-[0.8125rem]">
          {TABLE_HEAD}
          <tbody>
            {data.lines.map((line) => (
              <RunLineRows
                key={line.id}
                line={line}
                open={activeLine === line.id}
                onToggle={() => setActiveLine((was) => (was === line.id ? null : line.id))}
              />
            ))}
          </tbody>
        </table>
      </div>
      <TenancyChecks
        lines={data.tenancyLines}
        compared={data.compared}
        row={(line) => (
          <RunTenancyLineRows
            key={line.id}
            line={line}
            open={activeLine === line.id}
            onToggle={() => setActiveLine((was) => (was === line.id ? null : line.id))}
          />
        )}
      />
      <p className="border-t border-rule px-5 py-3 font-mono text-[0.6875rem] break-all text-ink-subtle">
        {/* The files this run was made from. Somebody holding one can show it is the same one. */}
        owner balances sha256 {data.fileSha256}
        {data.paidToFileSha256 ? (
          <span className="block">paid-to dates sha256 {data.paidToFileSha256}</span>
        ) : null}
        {data.bondsFileSha256 ? (
          <span className="block">bonds sha256 {data.bondsFileSha256}</span>
        ) : null}
      </p>
    </Panel>
  );
}

function RunLineRows({
  line,
  open,
  onToggle,
}: {
  line: RunLine;
  open: boolean;
  onToggle: () => void;
}) {
  const differs = line.status !== 'agrees';
  const explanation = line.notes[0];

  return (
    <>
      <tr className="border-t border-rule">
        <td className="px-5 py-2">
          <LineSubject line={line} />
        </td>
        <LineFigures line={line} />
        <td className="px-5 py-2">
          <span className="flex flex-wrap items-center gap-2">
            <Chip tone={differs ? (explanation ? 'neutral' : 'warn') : 'positive'}>
              {differs && explanation ? explanation.reasonLabel : line.statusLabel}
            </Chip>
            {differs ? (
              <Button size="sm" variant="ghost" onClick={onToggle}>
                {open ? 'Close' : explanation ? 'Review' : 'Explain'}
              </Button>
            ) : null}
          </span>
        </td>
      </tr>
      {open ? (
        <tr className="bg-surface-sunk">
          <td colSpan={5} className="px-5 py-3">
            <LineWork line={line} />
          </td>
        </tr>
      ) : null}
    </>
  );
}

function RunTenancyLineRows({
  line,
  open,
  onToggle,
}: {
  line: RunTenancyLine;
  open: boolean;
  onToggle: () => void;
}) {
  const differs = line.status !== 'agrees';
  const explanation = line.notes[0];

  return (
    <>
      <tr className="border-t border-rule">
        <td className="px-5 py-2">
          <TenancySubject line={line} />
        </td>
        <TenancyFigures line={line} />
        <td className="px-5 py-2">
          <span className="flex flex-wrap items-center gap-2">
            <Chip tone={differs ? (explanation ? 'neutral' : 'warn') : 'positive'}>
              {differs && explanation ? explanation.reasonLabel : line.statusLabel}
            </Chip>
            {differs ? (
              <Button size="sm" variant="ghost" onClick={onToggle}>
                {open ? 'Close' : explanation ? 'Review' : 'Explain'}
              </Button>
            ) : null}
          </span>
        </td>
      </tr>
      {open ? (
        <tr className="bg-surface-sunk">
          <td colSpan={5} className="px-5 py-3">
            <TenancyLineWork line={line} />
          </td>
        </tr>
      ) : null}
    </>
  );
}

type Note = RunLine['notes'][number];

/** What has been recorded against a line, newest first. The first is the explanation. */
function NotesTrail({ notes }: { notes: readonly Note[] }) {
  if (notes.length === 0) return null;
  return (
    <ul className="flex flex-col gap-1">
      {notes.map((entry, index) => (
        <li key={entry.createdAt} className={index === 0 ? '' : 'text-ink-subtle'}>
          <span className="font-semibold">{entry.reasonLabel}</span> — {entry.note}
          <span className="text-ink-subtle">
            {entry.notedByName ? ` · ${entry.notedByName}` : ''}
            {index === 0 ? '' : ' · superseded'}
          </span>
        </li>
      ))}
    </ul>
  );
}

/** Where what was found gets written down — the same form for an owner's money and a tenancy's. */
function ExplainForm({
  pending,
  placeholder,
  onRecord,
}: {
  pending: boolean;
  placeholder: string;
  onRecord: (finding: { reason: Reason; note: string }, done: () => void) => void;
}) {
  const [reason, setReason] = useState<Reason>('timing');
  const [note, setNote] = useState('');

  return (
    <>
      <form
        className="flex flex-wrap items-end gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          onRecord({ reason, note }, () => setNote(''));
        }}
      >
        <Field label="What was found">
          {(props) => (
            <Select
              {...props}
              value={reason}
              onChange={(e) => setReason(e.target.value as Reason)}
              className="w-48"
            >
              {REASONS.map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field label="In a sentence" className="min-w-0 flex-1">
          {(props) => (
            <Input
              {...props}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={placeholder}
            />
          )}
        </Field>
        <Button type="submit" size="sm" disabled={pending}>
          {pending ? 'Recording' : 'Record'}
        </Button>
      </form>
      <p className="text-[0.75rem] text-ink-muted">
        An explanation changes nothing here. If this system is wrong, put it right the ordinary way
        and run the comparison again.
      </p>
    </>
  );
}

/** After a note lands, the run and the list of runs both say something different. */
function useRefreshRun() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: trpc.parallelRun.get.queryKey() });
    void queryClient.invalidateQueries({ queryKey: trpc.parallelRun.status.queryKey() });
  };
}

/**
 * Working one difference: what this system did to the balance since cutover, to read against the
 * old system's ledger for the same owner, and where what was found gets written down.
 */
function LineWork({ line }: { line: RunLine }) {
  const refresh = useRefreshRun();
  const activity = useQuery(trpc.parallelRun.activity.queryOptions({ lineId: line.id }));
  const record = useMutation(
    trpc.parallelRun.note.mutationOptions({ meta: { errorTitle: 'Could not record that' } }),
  );

  return (
    <div className="flex flex-col gap-3 text-[0.8125rem]">
      <NotesTrail notes={line.notes} />

      <div>
        <p className="mb-1 font-semibold">
          What this system recorded
          {activity.data?.from
            ? `, after ${formatNzDate(activity.data.from)} to ${formatNzDate(activity.data.to)}`
            : ''}
        </p>
        {activity.isPending ? (
          <p className="text-ink-muted">Loading…</p>
        ) : activity.error ? (
          <LoadFailed retry={() => void activity.refetch()} retrying={activity.isFetching} />
        ) : activity.data.entries.length === 0 ? (
          <p className="text-ink-muted">
            Nothing since the opening balance. If the old system moved, the cause is on its side.
          </p>
        ) : (
          <table className="w-full max-w-3xl">
            <tbody>
              {activity.data.entries.map((entry) => (
                <tr key={entry.entryId} className="border-t border-rule first:border-t-0">
                  <td className="py-1 pr-3 whitespace-nowrap tabular-nums">
                    {formatNzDate(entry.date)}
                  </td>
                  <td className="py-1 pr-3 font-mono text-[0.75rem]">{entry.transactionRef}</td>
                  <td className="py-1 pr-3">{entry.narrative}</td>
                  <td className="py-1 text-right">
                    <Amount value={BigInt(entry.amountCents)} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <ExplainForm
        pending={record.isPending}
        placeholder="The old system took its fee a day later"
        onRecord={(finding, done) =>
          record.mutate(
            { lineId: line.id, ...finding },
            {
              onSuccess() {
                refresh();
                done();
              },
            },
          )
        }
      />
    </div>
  );
}

/**
 * Working a paid-to date or a bond that differs. No list of entries here: the tenancy's own page
 * has its rent and its bond, and is where the tracing is done.
 */
function TenancyLineWork({ line }: { line: RunTenancyLine }) {
  const refresh = useRefreshRun();
  const record = useMutation(
    trpc.parallelRun.noteTenancyLine.mutationOptions({
      meta: { errorTitle: 'Could not record that' },
    }),
  );

  return (
    <div className="flex flex-col gap-3 text-[0.8125rem]">
      <NotesTrail notes={line.notes} />
      <ExplainForm
        pending={record.isPending}
        placeholder={
          line.kind === 'paid_to'
            ? 'A part payment the old system counts as days'
            : 'Lodged on the 3rd; the old system still shows it held'
        }
        onRecord={(finding, done) =>
          record.mutate(
            { tenancyLineId: line.id, ...finding },
            {
              onSuccess() {
                refresh();
                done();
              },
            },
          )
        }
      />
    </div>
  );
}
